import React, { useState, useEffect, useCallback } from 'react';
import { policyAPI } from '../../../services';
import { FileCheck, CheckCircle, Clock, ExternalLink, RefreshCw } from 'lucide-react';

const CAT_LABELS = { hr:'HR Policy', it:'IT Policy', finance:'Finance', operations:'Operations', compliance:'Compliance', safety:'Safety', code_of_conduct:'Code of Conduct', leave:'Leave Policy', other:'Other' };

const MyPoliciesPage = () => {
  const [policies, setPolicies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState(null);

  const showToast = (msg, type='success') => { setToast({ msg, type }); setTimeout(() => setToast(null), 3000); };

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const res = await policyAPI.getMyPolicies();
      setPolicies(Array.isArray(res?.data) ? res.data : []);
    } catch { showToast('Failed to load', 'error'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleAcknowledge = async (id) => {
    if (!window.confirm('I confirm that I have read and understood this policy.')) return;
    try {
      await policyAPI.acknowledgePolicy(id);
      showToast('Policy acknowledged');
      load();
    } catch { showToast('Failed to acknowledge', 'error'); }
  };

  const pending = policies.filter(p => !p.isAcknowledged);
  const acknowledged = policies.filter(p => p.isAcknowledged);

  return (
    <div className="p-6 max-w-4xl mx-auto">
      {toast && <div className={`fixed top-4 right-4 z-50 px-4 py-3 rounded-lg shadow-lg text-white text-sm ${toast.type==='error'?'bg-red-500':'bg-green-500'}`}>{toast.msg}</div>}

      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2"><FileCheck className="w-6 h-6 text-blue-600"/>Company Policies</h1>
          <p className="text-sm text-gray-500 mt-1">Read and acknowledge your assigned policies</p>
        </div>
        <button onClick={load} className="p-2 border border-gray-300 rounded-lg hover:bg-gray-50"><RefreshCw className="w-4 h-4"/></button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-4 mb-6">
        {[
          { label:'Total', value:policies.length, color:'text-blue-700', bg:'bg-blue-50' },
          { label:'Pending', value:pending.length, color:'text-red-600', bg:'bg-red-50' },
          { label:'Acknowledged', value:acknowledged.length, color:'text-green-700', bg:'bg-green-50' }
        ].map(({ label, value, color, bg }) => (
          <div key={label} className={`${bg} rounded-xl p-4 text-center`}>
            <p className={`text-2xl font-bold ${color}`}>{value}</p>
            <p className="text-xs text-gray-500 mt-0.5">{label}</p>
          </div>
        ))}
      </div>

      {loading ? <div className="text-center py-12 text-gray-400">Loading...</div>
      : policies.length === 0 ? (
        <div className="flex flex-col items-center py-16 text-gray-400 bg-white rounded-xl border border-gray-200">
          <FileCheck className="w-12 h-12 mb-3 opacity-30"/>
          <p>No policies assigned to you</p>
        </div>
      ) : (
        <>
          {pending.length > 0 && (
            <>
              <p className="text-sm font-semibold text-red-600 mb-3 flex items-center gap-1"><Clock className="w-4 h-4"/>Pending Acknowledgement ({pending.length})</p>
              <div className="space-y-3 mb-6">
                {pending.map(p => (
                  <div key={p._id} className="bg-white rounded-xl border-2 border-orange-200 p-5">
                    <div className="flex items-start justify-between gap-3 mb-2">
                      <div>
                        <p className="font-semibold text-gray-900">{p.title}</p>
                        <p className="text-xs text-gray-500">{CAT_LABELS[p.category]}{p.dueDate && ` · Due: ${new Date(p.dueDate).toLocaleDateString('en-IN')}`}</p>
                      </div>
                      <span className="px-2 py-1 rounded-full text-xs font-semibold bg-orange-100 text-orange-700">Pending</span>
                    </div>
                    {p.description && <p className="text-sm text-gray-600 mb-3">{p.description}</p>}
                    <div className="flex items-center gap-3">
                      {p.fileUrl && <a href={p.fileUrl} target="_blank" rel="noreferrer" className="flex items-center gap-1 text-xs text-blue-600 hover:underline"><ExternalLink className="w-3 h-3"/>View Policy</a>}
                      <button onClick={() => handleAcknowledge(p._id)} className="ml-auto flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700">
                        <CheckCircle className="w-4 h-4"/>I Acknowledge
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}

          {acknowledged.length > 0 && (
            <>
              <p className="text-sm font-semibold text-green-700 mb-3 flex items-center gap-1"><CheckCircle className="w-4 h-4"/>Acknowledged ({acknowledged.length})</p>
              <div className="space-y-2">
                {acknowledged.map(p => (
                  <div key={p._id} className="bg-white rounded-xl border border-gray-200 p-4 flex items-center gap-3">
                    <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0"/>
                    <div className="flex-1">
                      <p className="font-medium text-gray-900">{p.title}</p>
                      <p className="text-xs text-gray-400">{CAT_LABELS[p.category]}</p>
                    </div>
                    {p.fileUrl && <a href={p.fileUrl} target="_blank" rel="noreferrer" className="text-xs text-blue-600 hover:underline flex items-center gap-1"><ExternalLink className="w-3 h-3"/>View</a>}
                  </div>
                ))}
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
};

export default MyPoliciesPage;
